import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { name: "Home", path: "/" },
  {
    name: "Corporates We Serve",
    path: "/solutions",
    dropdown: [
      { name: "Pan-India Modular Manufacturing", path: "/modular-office-furniture-manufacturer" },
      { name: "IT & Technology", path: "/solutions/it-technology" },
      { name: "Corporate HQ", path: "/solutions/corporate-hq" },
      { name: "Co-working Spaces", path: "/solutions/coworking" },
      { name: "BPO / Call Centers", path: "/solutions/bpo" },
      { name: "Startups", path: "/solutions/startups" },
      { name: "Banking & Finance", path: "/solutions/banking-finance" },
      { name: "Office Partitions & Cabin Systems", path: "/solutions/office-partitions" },
      { name: "View All Industries →", path: "/solutions" },
    ],
  },
  { name: "Office Furniture We Make", path: "/products" },
  { name: "About Us", path: "/about", dropdown: [
      { name: "Our Story", path: "/about" },
      { name: "Why Choose Ezeefit", path: "/why-choose-ezeefit" },
      { name: "Buyer's Guide", path: "/buyers-guide" },
    ],
  },
  { name: "Case Studies", path: "/case-studies" },
  { name: "Manufacturing", path: "/manufacturing", dropdown: [
      { name: "Our Bhiwandi Factory", path: "/manufacturing" },
      { name: "Mumbai Manufacturing", path: "/office-furniture-mumbai" },
    ],
  },
  { name: "Process", path: "/process" },
  {
    name: "Partner",
    path: "/partners",
    dropdown: [
      { name: "Architects & Consultants", path: "/partners" },
      { name: "Become a Distributor", path: "/distributors" },
      { name: "Blog & Insights", path: "/blog" },
    ],
  },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-md"
          : "bg-white/95 backdrop-blur-md shadow-sm"
      }`}
    >
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-6 lg:pr-4 xl:px-6 xl:pr-4 2xl:px-10 2xl:pr-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo — new circular brand mark (2026). ® and © are baked into the
              artwork itself, positioned outside the circle at top-right. */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 mr-2 sm:mr-4 xl:mr-3 2xl:mr-8 min-w-0 flex-1 xl:flex-none" data-testid="logo">
            <picture style={{ display: "contents" }}>
              <source srcSet="/media/logo-2026.webp" type="image/webp" />
              <img
                src="/media/logo-2026.png"
                alt="Ezeefit Office Systems - Modular Office Furniture Manufacturer Mumbai"
                width="1059"
                height="992"
                className="h-14 sm:h-16 w-auto object-contain flex-shrink-0"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                data-testid="navbar-logo-image"
              />
            </picture>
            <div className="flex flex-col leading-none border-l border-[#E2E8F0] pl-2 sm:pl-3 min-w-0 flex-1">
              <span className="text-xs sm:text-sm font-bold text-[#0F172A] tracking-tight leading-tight truncate">Ezeefit Office Systems</span>
              <span className="text-[9px] sm:text-[10px] text-[#64748B] tracking-wide leading-tight mt-0.5" data-testid="since-tagline">Since 2014</span>
              <span className="text-[9px] sm:text-[10px] text-[#64748B] tracking-wide leading-tight hidden 2xl:inline" data-testid="legal-entity-tagline">A brand of Ezeefit Modular Furniture Pvt. Ltd.</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-3 2xl:gap-4">
            {navLinks.map((link) =>
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-semibold text-[#0F172A] hover:text-[#F97316] transition-colors whitespace-nowrap">
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {link.dropdown.map((item) => (
                      <DropdownMenuItem key={item.name} asChild>
                        <Link
                          to={item.path}
                          className="w-full cursor-pointer"
                          data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm font-semibold transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? "text-[#F97316]"
                      : "text-[#0F172A] hover:text-[#F97316]"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden xl:flex items-center gap-3 flex-shrink-0 ml-3 2xl:ml-5">
            {/* Certification badge — IGBC Indian Green Building Council (circular, +15% size, non-clickable) */}
            <div
              className="hidden xl:flex items-center justify-center shrink-0 mr-1 bg-white rounded-full border border-[#E2E8F0] shadow-sm"
              aria-label="IGBC Indian Green Building Council member"
              data-testid="nav-cert-logos"
              style={{ width: "56px", height: "56px" }}
            >
              <img
                src="/certifications/igbc.png"
                alt="IGBC Indian Green Building Council"
                className="w-auto object-contain"
                style={{ height: "46px" }}
                loading="lazy"
                decoding="async"
              />
            </div>
            <Link
              to="/portal/login"
              className="text-sm font-semibold text-[#0F172A] hover:text-[#F97316] transition-colors whitespace-nowrap"
              data-testid="nav-client-portal"
            >
              Client Portal
            </Link>
            <Button
              asChild
              className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-4 2xl:px-6 py-2 text-sm font-semibold uppercase tracking-wider"
              data-testid="nav-enquiry-btn"
            >
              <Link to="/contact">
                <span className="hidden 2xl:inline">Request Consultation</span>
                <span className="2xl:hidden">Enquire Now</span>
              </Link>
            </Button>
          </div>

          {/* Mobile IGBC badge — visible in mobile top bar next to hamburger (circular, +15% logo, non-clickable) */}
          <div
            className="xl:hidden flex items-center justify-center shrink-0 bg-white rounded-full border border-[#E2E8F0] shadow-sm mr-2"
            aria-label="IGBC Indian Green Building Council member"
            data-testid="mobile-nav-cert-logos-inline"
            style={{ width: "44px", height: "44px" }}
          >
            <img
              src="/certifications/igbc.png"
              alt="IGBC Indian Green Building Council"
              className="w-auto object-contain"
              style={{ height: "36px" }}
              loading="lazy"
              decoding="async"
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#0F172A]" />
            ) : (
              <Menu className="w-6 h-6 text-[#0F172A]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-[#E2E8F0] max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Certification badge — circular IGBC (visible at top of mobile drawer, non-clickable) */}
            <div
              className="flex items-center justify-center mx-auto bg-white rounded-full border border-[#E2E8F0] shadow-sm"
              aria-label="IGBC Indian Green Building Council member"
              data-testid="mobile-nav-cert-logos"
              style={{ width: "56px", height: "56px" }}
            >
              <img src="/certifications/igbc.png" alt="IGBC Indian Green Building Council" className="w-auto object-contain" style={{ height: "46px" }} loading="lazy" decoding="async" />
            </div>
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name} className="space-y-2">
                  <span className="block text-sm font-semibold text-[#0F172A]">
                    {link.name}
                  </span>
                  <div className="pl-4 space-y-2">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block text-sm text-[#64748B] hover:text-[#0F172A]"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block text-sm font-medium ${
                    isActive(link.path)
                      ? "text-[#0F172A]"
                      : "text-[#64748B] hover:text-[#0F172A]"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <div className="pt-3 border-t border-[#E2E8F0] space-y-3">
              <Link
                to="/portal/login"
                className="block w-full text-center text-sm font-semibold text-[#0F172A] py-2.5 border-2 border-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors"
                data-testid="mobile-login-btn"
              >
                Client Portal Login
              </Link>
              <Button
                asChild
                className="w-full bg-[#F97316] hover:bg-orange-600 text-white rounded-none"
              >
                <Link to="/contact">Request Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
